import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory } from 'react-router-dom';
import { 
    BasicInformationStep,
    AddressInformationStep,
    SpacesStep,
    ContactInformationStep,
    DocumentsAndPoliciesStep,
    VenueCapabilitiesStep
} from './steps';
import { LoadScript } from '@react-google-maps/api';
import { getBackendUserUrl } from 'utils/utils';

/**
 * Main venue form componentaddOnId
 */
const VenueOnboardingForm = () => {
    const history = useHistory();
    // Step state for pagination
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Basic Information', 'Address Information', 'Spaces', 'Contact Information', 'Venue Capabilities', 'Documents and Policies'];

    // Step 1: Basic Information
    const [venueName, setVenueName] = useState('');
    const [venueDescription, setVenueDescription] = useState('');
    const [coverPhoto, setCoverPhoto] = useState(null);
    const [otherPhotos, setOtherPhotos] = useState([]);
    const [features, setFeatures] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [website, setWebsite] = useState('');
    const [instagram, setInstagram] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [facebook, setFacebook] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const validateStep1 = () => {
        const newErrors = {};
        let isValid = true;
        
        if (!venueName || venueName.trim() === '') {
            newErrors.venueName = 'Venue name is required';
            isValid = false;
        }
        
        if (!venueDescription || venueDescription.trim() === '') {
            newErrors.venueDescription = 'Venue description is required';
            isValid = false;
        }
        
        if (!coverPhoto) {
            newErrors.coverPhoto = 'Cover photo is required';
            isValid = false;
        }

        // Validate email format if provided
        if (email && email.trim() !== '') {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                newErrors.email = 'Please enter a valid email address';
                isValid = false;
            }
        }

        // Validate website URL format if provided
        if (website && website.trim() !== '') {
            // Allow URLs with or without protocol, with or without www
            const websitePattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?$/;
            if (!websitePattern.test(website)) {
                newErrors.website = 'Please enter a valid website URL (e.g., example.com or www.example.com)';
                isValid = false;
            }
        }

        // Validate social media URLs if provided
        if (instagram && instagram.trim() !== '') {
            // Allow instagram.com/username or instagram.com/username/ or just username
            const instagramPattern = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+(\/)?$|^[a-zA-Z0-9._]+$/;
            if (!instagramPattern.test(instagram)) {
                newErrors.instagram = 'Please enter a valid Instagram username or URL (e.g., username or instagram.com/username)';
                isValid = false;
            }
        }

        if (linkedin && linkedin.trim() !== '') {
            // Allow linkedin.com/in/username or linkedin.com/company/companyname or just username/companyname
            const linkedinPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9-]+(\/)?$|^[a-zA-Z0-9-]+$/;
            if (!linkedinPattern.test(linkedin)) {
                newErrors.linkedin = 'Please enter a valid LinkedIn profile or company URL (e.g., linkedin.com/in/username or linkedin.com/company/companyname)';
                isValid = false;
            }
        }

        if (facebook && facebook.trim() !== '') {
            // Allow facebook.com/username or facebook.com/pages/username or just username
            const facebookPattern = /^(https?:\/\/)?(www\.)?facebook\.com\/([a-zA-Z0-9.-]+)(\/)?$|^[a-zA-Z0-9.-]+$/;
            if (!facebookPattern.test(facebook)) {
                newErrors.facebook = 'Please enter a valid Facebook username or URL (e.g., facebook.com/username)';
                isValid = false;
            }
        }
        
        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));
        
        return isValid;
    };


    // Step 2: Address Information
    const [mainAddress, setMainAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [country, setCountry] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [serviceEntryAddress, setServiceEntryAddress] = useState('');
    const [parkingInstructions, setParkingInstructions] = useState('');
    const [mailingAddress, setMailingAddress] = useState('');
    const [addressPhotos, setAddressPhotos] = useState([]);
    const [specialInstructions, setSpecialInstructions] = useState('');
    // Validate address information
    const validateStep2 = () => {
        const newErrors = {};
        let isValid = true;
        
        if (!mainAddress || mainAddress.trim() === '') {
            newErrors.mainAddress = 'Main address is required';
            isValid = false;
        }
        
        if (!city || city.trim() === '') {
            newErrors.city = 'City is required';
            isValid = false;
        }
        
        if (!state || state.trim() === '') {
            newErrors.state = 'State/Province is required';
            isValid = false;
        }
        
        if (!zipCode || zipCode.trim() === '') {
            newErrors.zipCode = 'ZIP/Postal code is required';
            isValid = false;
        }
        
        if (!country || country.trim() === '') {
            newErrors.country = 'Country is required';
            isValid = false;
        }
        
        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));
        
        return isValid;
    };


    //Step 3: Spaces
    const [spaces, setSpaces] = useState([]);

    // Validate spaces
    const validateStep3 = () => {
        const newErrors = {};
        let isValid = true;
        
        if (!spaces || spaces.length === 0) {
            newErrors.spaces = 'At least one space is required';
            isValid = false;
        }

        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));
        
        return isValid;
    };

    // Step 4: Contact Information
    const [contacts, setContacts] = useState([
        {
            id: 'primary-contact',
            firstName: '',
            lastName: '',
            role: '',
            email: '',
            phone: '',
            isPrimary: true
        }
    ]);
    
    // Update handleContactChange to support the 'replace_all' pattern like handleSpaceChange
    const handleContactChange = (contactId, field, value) => {
        // If contactId is null and field is 'replace_all', we're replacing the entire contacts array
        if (contactId === null && field === 'replace_all') {
            setContacts(value);
            return;
        }
        
        // Otherwise, handle normal property changes for a specific contact
        setContacts(contacts.map(contact => 
            contact.id === contactId ? { ...contact, [field]: value } : contact
        ));
    };

    // Step 5: Venue capabilities
    const [servesAlcohol, setServesAlcohol] = useState(false);
    const [providesCatering, setProvidesCatering] = useState(false);
    const [providesInHouseSecurity, setProvidesInHouseSecurity] = useState(false);

    // Define document types with metadata
    const documentTypes = {
        INSURANCE: {
            key: 'insurance',
            label: 'Insurance Certificates',
            description: 'General Liability, Automobile Insurance (if transporting goods), Worker\'s compensation',
            isRequired: true,
            acceptedTypes: ['.pdf', '.jpg', '.jpeg', '.png']
        },
        HEALTH_SAFETY: {
            key: 'healthSafety',
            label: 'Proof of Health and Safety Compliance',
            description: 'Health department certificates, fire safety inspection reports',
            isRequired: true,
            acceptedTypes: ['.pdf', '.jpg', '.jpeg', '.png']
        },
        PARKING_ACCESS: {
            key: 'parkingAccess',
            label: 'Parking and Access Agreements',
            description: 'Defines where vendors can use load docks and which entrances they can use',
            isRequired: true,
            acceptedTypes: ['.pdf', '.doc', '.docx']
        },
        LIABILITY_WAIVERS: {
            key: 'liabilityWaivers',
            label: 'Liability Waivers',
            description: 'Standard liability waivers for vendors and event participants',
            isRequired: true,
            acceptedTypes: ['.pdf', '.doc', '.docx']
        },
        NOISE_GUIDELINES: {
            key: 'noiseGuidelines',
            label: 'Noise and Environment Guidelines',
            description: 'Noise restrictions, curfew times for DJs and music',
            isRequired: false,
            acceptedTypes: ['.pdf', '.doc', '.docx']
        },
        EQUIPMENT_APPROVAL: {
            key: 'equipmentApproval',
            label: 'Equipment and Setup Approval',
            description: 'Guidelines for equipment setup and approval process',
            isRequired: false,
            acceptedTypes: ['.pdf', '.doc', '.docx']
        },
        NON_COMPETE: {
            key: 'nonCompete',
            label: 'Non-Compete Agreement',
            description: 'Non-Compete / Non-Solicitation Agreement (Optional)',
            isRequired: false,
            acceptedTypes: ['.pdf', '.doc', '.docx']
        },
        SUBCONTRACTOR: {
            key: 'subcontractor',
            label: 'Subcontractor Agreement',
            description: 'Agreement for subcontractors (if applicable)',
            isRequired: false,
            acceptedTypes: ['.pdf', '.doc', '.docx']
        },
        ALCOHOL_LICENSE: {
            key: 'alcoholLicense',
            label: 'Alcohol License',
            description: 'License to serve alcohol',
            isRequired: false,
            acceptedTypes: ['.pdf', '.jpg', '.jpeg', '.png']
        },
        CATERING_LICENSE: {
            key: 'cateringLicense',
            label: 'Catering License',
            description: 'License to provide catering services',
            isRequired: false,
            acceptedTypes: ['.pdf', '.jpg', '.jpeg', '.png']
        }
    };

    // Initialize documents state with metadata
    const [documents, setDocuments] = useState(
        Object.entries(documentTypes).reduce((acc, [key, type]) => ({
            ...acc,
            [type.key]: {
                files: [],
                type: type.key,
                label: type.label,
                description: type.description,
                isRequired: type.isRequired,
                acceptedTypes: type.acceptedTypes
            }
        }), {})
    );

    // Form validation
    const [errors, setErrors] = useState({});

    // Add loading state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Helper function to determine the next route after venue onboarding
    const getNextRoute = () => {
        try {
            // Check if there are stored company types from the previous step
            const storedCompanyTypes = sessionStorage.getItem('selectedCompanyTypes');
            if (storedCompanyTypes) {
                const companyTypes = JSON.parse(storedCompanyTypes);
                
                // Check if user also selected package service types
                const hasPackageServices = companyTypes.some(type => 
                    ['Catering', 'Sponsor', 'Other Service Provider'].includes(type)
                );
                
                if (hasPackageServices) {
                    return '/packagesOnboardingForm';
                }
            }
            // Default to myevents if no other onboarding needed
            return '/myevents';
        } catch (error) {
            console.error('Error determining next route:', error);
            return '/myevents'; // Default fallback
        }
    };

    // Update handleSubmit to include document metadata
    const handleSubmit = async () => {
        if (isSubmitting) return; // Prevent multiple submissions
        
        if (!validateStep5()) {
            toast.error('Please upload all required licenses');
            return;
        }

        setIsSubmitting(true); // Start loading

        try {
            // Get company ID from session storage
            const companyId = sessionStorage.getItem('companyId');
            
            // 1. Prepare venue data (excluding spaces)
            const venueData = {
                venueName,
                venueDescription,
                features,
                eventTypes,
                website,
                instagram,
                linkedin,
                facebook,
                email,
                phoneNumber,
                mainAddress,
                city,
                state,
                zipCode,
                country,
                neighborhood,
                deliveryAddress: deliveryAddress || mainAddress,
                serviceEntryAddress: serviceEntryAddress || mainAddress,
                parkingInstructions,
                mailingAddress: mailingAddress || mainAddress,
                specialInstructions,
                contacts,
                servesAlcohol,
                providesCatering,
                providesInHouseSecurity,
                isActive: true,
                isApproved: false,
                createdAt: new Date(),
                updatedAt: new Date(),
                companyId // Add company ID to venue data
            };

            // 2. Prepare FormData for venue (excluding spaces)
            const venueFormData = new FormData();
            venueFormData.append('venue', JSON.stringify(venueData));
            
            // Add cover photo
            if (coverPhoto instanceof File) {
                venueFormData.append('coverPhoto', coverPhoto);
            }
            
            // Add other photos
            otherPhotos.forEach((photo) => {
                if (photo instanceof File) {
                    venueFormData.append('otherPhotos', photo);
                }
            });
            
            // Add address photos
            addressPhotos.forEach((photo) => {
                if (photo instanceof File) {
                    venueFormData.append('addressPhotos', photo);
                }
            });
            
            // Add documents with their types
            Object.entries(documents).forEach(([key, doc]) => {
                if (doc.files && Array.isArray(doc.files) && doc.files.length > 0) {
                    doc.files.forEach((file) => {
                        if (file instanceof File) {
                            venueFormData.append('documents', file);
                            venueFormData.append('documentTypes', key);
                        }
                    });
                }
            });

            let guid = null;
            const backendUrl = getBackendUserUrl() + '/venue';
            const venueRes = await fetch(backendUrl + '/add_venue', {
                method: 'POST',
                body: venueFormData
            });
            
            if (!venueRes.ok) {
                const errorData = await venueRes.json();
                throw new Error(errorData.detail || 'Failed to create venue');
            }
            
            const venueJson = await venueRes.json();
            guid = venueJson.guid;
            
            if (!guid) {
                throw new Error('No guid returned from backend');
            }
            
            toast.success('Venue details submitted!');

            // 4. Prepare FormData for spaces
            const spacesFormData = new FormData();
            // Remove file objects from spaces for JSON
            const spacesForJson = spaces.map((space) => {
                const { images, floorplanImage, ...rest } = space;
                return { ...rest };
            });
            spacesFormData.append('spaces', JSON.stringify(spacesForJson));
            
            // Add all space images and floorplans with their space IDs
            spaces.forEach((space) => {
                if (space.images && Array.isArray(space.images)) {
                    space.images.forEach((img) => {
                        if (img instanceof File) {
                            spacesFormData.append('spaceImages', img);
                            spacesFormData.append('spaceImageIds', space.id); // Add space ID for each image
                        }
                    });
                }
                if (space.floorplanImage instanceof File) {
                    spacesFormData.append('floorplanImages', space.floorplanImage);
                    spacesFormData.append('floorplanImageIds', space.id); // Add space ID for each floorplan
                }
            });

            // 5. Send spaces data to backend
            const spacesRes = await fetch(backendUrl + `/add_spaces?guid=${guid}`, {
                method: 'POST',
                body: spacesFormData
            });
            
            if (!spacesRes.ok) {
                const errorData = await spacesRes.json();
                throw new Error(errorData.detail || 'Failed to submit spaces');
            }
            
            toast.success('Venue spaces submitted!');
            
            // 6. Determine next route and redirect
            const nextRoute = getNextRoute();
            history.push(nextRoute);
        } catch (error) {
            console.error('Error submitting venue:', error);
            toast.error(error.message || 'Failed to submit venue. Please try again.');
        } finally {
            setIsSubmitting(false); // End loading
        }
    };

    // Update memoizedHandleDocumentChange to work with new structure
    const memoizedHandleDocumentChange = useCallback((documentType, files) => {
        setDocuments(prevDocuments => ({
            ...prevDocuments,
            [documentType]: {
                ...prevDocuments[documentType],
                files: files
            }
        }));
    }, []);
    
    // Validate contact information
    const validateStep4 = () => {
        const newErrors = {};
        let isValid = true;
        
        // Check if there are any contacts
        if (!contacts || contacts.length === 0) {
            newErrors.contacts = 'At least one contact is required';
            isValid = false;
            setErrors(prevErrors => ({
                ...prevErrors,
                ...newErrors
            }));
            return isValid;
        }
        
        // Check if primary contact has all required fields
        const primaryContact = contacts.find(c => c.isPrimary);
        
        if (!primaryContact) {
            newErrors.contacts = 'A primary contact is required';
            isValid = false;
        } else {
            // Check required fields for primary contact
            if (!primaryContact.firstName || 
                !primaryContact.lastName || 
                !primaryContact.role ||
                !primaryContact.email || 
                !primaryContact.phone) {
                newErrors.contacts = 'All primary contact information is required';
                isValid = false;
            }
        }
        
        // Validate email format for all contacts that have an email entered
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = contacts.filter(c => 
            c.email && !emailRegex.test(c.email)
        );
        
        if (invalidEmails.length > 0) {
            newErrors.contactsEmail = 'Please enter valid email addresses';
            isValid = false;
        }
        
        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));
        
        return isValid;
    };
    
    // Validate venue capabilities (now step 4)
    const validateStep5 = () => {
        const newErrors = {};
        let isValid = true;

        // Check alcohol license if venue serves alcohol
        if (servesAlcohol) {
            const alcoholLicense = documents.alcoholLicense;
            if (!alcoholLicense || !alcoholLicense.files || alcoholLicense.files.length === 0) {
                newErrors.alcoholLicense = 'Alcohol license is required when venue serves alcohol';
                isValid = false;
            }
        }

        // Check catering license if venue provides catering
        if (providesCatering) {
            const cateringLicense = documents.cateringLicense;
            if (!cateringLicense || !cateringLicense.files || cateringLicense.files.length === 0) {
                newErrors.cateringLicense = 'Catering license is required when venue provides catering';
                isValid = false;
            }
        }

        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));
        return isValid;
    };
    
    // Validate documents and policies (now step 5)
    const validateStep6 = () => {
        // Documents are optional for now
        return true;
    };

    // Handle next button click
    const handleNext = () => {
        switch (activeStep) {
            case 0: // Basic Information
                if (validateStep1()) {
                    setActiveStep(1);
                } else {
                    toast.error('Please complete all required basic information fields');
                }
                break;
            case 1: // Address Information
                if (validateStep2()) {
                    setActiveStep(2);
                } else {
                    toast.error('Please fill in all required address fields');
                }
                break;
            case 2: // Spaces
                if (validateStep3()) {
                    setActiveStep(3);
                } else {
                    toast.error('Please add at least one space to your venue');
                }
                break;
            case 3: // Contact Information
                if (validateStep4()) {
                    setActiveStep(4);
                } else {
                    toast.error('Please fill in all required contact information');
                }
                break;
            case 4: // Venue Capabilities
                if (validateStep5()) {
                    setActiveStep(5);
                } else {
                    toast.error('Please complete all required venue capability fields');
                }
                break;
            case 5: // Documents and Policies
                if (validateStep6()) {
                    handleSubmit();
                } else {
                    toast.error('Please upload all required documents');
                }
                break;
            default:
                break;
        }
    };

    // Handle back button click
    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };
    
    // Render the appropriate step based on activeStep
    const renderStep = () => {
        switch (activeStep) {
            case 0: // Basic Information
                return (
                    <BasicInformationStep 
                        venueName={venueName}
                        setVenueName={setVenueName}
                        venueDescription={venueDescription}
                        setVenueDescription={setVenueDescription}
                        coverPhoto={coverPhoto}
                        setCoverPhoto={setCoverPhoto}
                        otherPhotos={otherPhotos}
                        setOtherPhotos={setOtherPhotos}
                        features={features}
                        setFeatures={setFeatures}
                        eventTypes={eventTypes}
                        setEventTypes={setEventTypes}
                        website={website}
                        setWebsite={setWebsite}
                        instagram={instagram}
                        setInstagram={setInstagram}
                        linkedin={linkedin}
                        setLinkedin={setLinkedin}
                        facebook={facebook}
                        setFacebook={setFacebook}
                        email={email}
                        setEmail={setEmail}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        errors={errors}
                    />
                );
            case 1: // Address Information
                return (
                    <AddressInformationStep 
                        mainAddress={mainAddress}
                        setMainAddress={setMainAddress}
                        city={city}
                        setCity={setCity}
                        state={state}
                        setState={setState}
                        zipCode={zipCode}
                        setZipCode={setZipCode}
                        country={country}
                        setCountry={setCountry}
                        neighborhood={neighborhood}
                        setNeighborhood={setNeighborhood}
                        deliveryAddress={deliveryAddress}
                        setDeliveryAddress={setDeliveryAddress}
                        serviceEntryAddress={serviceEntryAddress}
                        setServiceEntryAddress={setServiceEntryAddress}
                        parkingInstructions={parkingInstructions}
                        setParkingInstructions={setParkingInstructions}
                        mailingAddress={mailingAddress}
                        setMailingAddress={setMailingAddress}
                        addressPhotos={addressPhotos}
                        setAddressPhotos={setAddressPhotos}
                        specialInstructions={specialInstructions}
                        setSpecialInstructions={setSpecialInstructions}
                        errors={errors}
                    />
                );
            case 2: // Spaces
                return (
                    <SpacesStep 
                        spaces={spaces}
                        handleSpaceChange={setSpaces}
                        errors={errors}
                    />
                );
            case 3: // Contact Information
                return (
                    <ContactInformationStep 
                        contacts={contacts}
                        handleContactChange={handleContactChange}
                        errors={errors}
                    />
                );
            case 4: // Venue Capabilities
                return (
                    <VenueCapabilitiesStep 
                        servesAlcohol={servesAlcohol}
                        setServesAlcohol={setServesAlcohol}
                        providesCatering={providesCatering}
                        setProvidesCatering={setProvidesCatering}
                        providesInHouseSecurity={providesInHouseSecurity}
                        setProvidesInHouseSecurity={setProvidesInHouseSecurity}
                        documents={documents}
                        memoizedHandleDocumentChange={memoizedHandleDocumentChange}
                        errors={errors}
                    />
                );
            case 5: // Documents and Policies
                return (
                    <DocumentsAndPoliciesStep 
                        documents={documents}
                        memoizedHandleDocumentChange={memoizedHandleDocumentChange}
                        errors={errors}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_PLACES_AUTOCOMPLETE_API_KEY}
            libraries={['places']}
        >
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: '70px', position: 'relative' }}>
                {/* Loading overlay */}
                {isSubmitting && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            zIndex: 9999,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ opacity: isSubmitting ? 0.5 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}>
                    {renderStep()}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                        {activeStep > 0 && (
                            <Button 
                                variant="outlined" 
                                onClick={handleBack}
                                sx={{ mr: 2 }}
                                disabled={isSubmitting}
                            >
                                Back
                            </Button>
                        )}
                        
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={handleNext}
                            disabled={isSubmitting}
                        >
                            {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </LoadScript>
    );
};

export default VenueOnboardingForm;
